/*
  Joylux landing page behavior. Everything here is an enhancement: without it every section
  still shows, carousels still swipe, and the pair buttons post to /cart/add.
  - [data-jx-reveal]: sections fade and slide in as they scroll into view
  - jx-marquee: pause button for the scrolling bands
  - jx-carousel: arrow buttons for the product carousels
  - jx-pair-add: adds two products in one request and opens Gloss's cart drawer
  Each joylux-* section includes this file, so it guards against running twice.
*/
(() => {
  if (window.jxLanding) return;
  window.jxLanding = true;

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const designMode = Boolean(window.Shopify && window.Shopify.designMode);

  // Reveal on scroll. Skipped in the theme editor so merchants always see their content.
  let observer = null;
  if (!reduceMotion && !designMode && 'IntersectionObserver' in window) {
    document.documentElement.classList.add('jx-anim');
    observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-in');
          observer.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.12 }
    );
  }

  const initReveal = (root) => {
    root.querySelectorAll('[data-jx-reveal]:not(.is-in)').forEach((element) => {
      if (observer) observer.observe(element);
      else element.classList.add('is-in');
    });
  };

  initReveal(document);
  document.addEventListener('shopify:section:load', (event) => initReveal(event.target));

  class JxMarquee extends HTMLElement {
    connectedCallback() {
      const button = this.querySelector('[data-jx-toggle]');
      if (!button || this.ready) return;
      this.ready = true;
      button.addEventListener('click', () => {
        const paused = this.querySelector('.jx-mq').classList.toggle('is-paused');
        button.setAttribute('aria-pressed', String(paused));
      });
    }
  }

  class JxCarousel extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-jx-track]');
      this.prev = this.querySelector('[data-jx-prev]');
      this.next = this.querySelector('[data-jx-next]');
      if (!this.track || this.ready) return;
      this.ready = true;

      this.prev?.addEventListener('click', () => this.go(-1));
      this.next?.addEventListener('click', () => this.go(1));
      this.track.addEventListener('scroll', () => this.update(), { passive: true });
      if ('ResizeObserver' in window) new ResizeObserver(() => this.update()).observe(this.track);
      this.update();
    }

    go(direction) {
      const item = this.track.firstElementChild;
      const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
      const step = item ? item.getBoundingClientRect().width + gap : this.track.clientWidth;
      this.track.scrollBy({ left: direction * step, behavior: reduceMotion ? 'auto' : 'smooth' });
    }

    update() {
      const max = this.track.scrollWidth - this.track.clientWidth;
      this.toggleAttribute('data-static', max <= 2);
      if (this.prev) this.prev.disabled = this.track.scrollLeft <= 2;
      if (this.next) this.next.disabled = this.track.scrollLeft >= max - 2;
    }
  }

  class JxPairAdd extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.form = this.querySelector('form');
      this.button = this.querySelector('[type="submit"]');
      if (!this.form || !this.button) return;
      this.ready = true;
      this.form.addEventListener('submit', this.onSubmit.bind(this));
    }

    onSubmit(event) {
      event.preventDefault();
      if (this.button.getAttribute('aria-disabled') === 'true') return;
      this.button.setAttribute('aria-disabled', 'true');
      this.button.classList.add('loading');

      const routes = window.routes || {};
      const cartItems = document.querySelector('cart-items');
      const drawer = cartItems && typeof cartItems.renderContents === 'function' ? cartItems : null;
      const items = [...this.form.querySelectorAll('input[name="items[][id]"]')].map((input) => ({
        id: Number(input.value),
        quantity: 1,
      }));

      const body = { items };
      if (drawer) {
        body.sections = drawer.getSectionsToRender().map((section) => section.section).filter(Boolean);
        body.sections_url = window.location.pathname;
      }

      fetch(`${routes.cart_add_url || '/cart/add'}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
      })
        .then((response) => response.json())
        .then((response) => {
          if (response.status) {
            this.reportError(response.description || response.message, response.errors);
            return;
          }

          // No cart drawer on the page: go to the cart, like Gloss's product-form does
          if (!drawer) {
            window.location = routes.cart_url || '/cart';
            return;
          }

          document.dispatchEvent(
            new CustomEvent('cart:update', { detail: { source: 'jx-pair-add', cartData: response } })
          );
          if (window.utils && window.accessibilityStrings) {
            window.utils.announce(window.accessibilityStrings.itemAdded);
          }
          drawer.renderContents(response);
        })
        .catch((error) => {
          console.error(error);
          this.reportError(window.cartStrings && window.cartStrings.error);
        })
        .finally(() => {
          this.button.classList.remove('loading');
          this.button.removeAttribute('aria-disabled');
        });
    }

    // Gloss's error-modal listens for cart:error and shows the message
    reportError(message, errors) {
      document.dispatchEvent(
        new CustomEvent('cart:error', {
          detail: { source: 'jx-pair-add', errors, message: message || 'Something went wrong. Please try again.' },
        })
      );
    }
  }

  if (!customElements.get('jx-marquee')) customElements.define('jx-marquee', JxMarquee);
  if (!customElements.get('jx-carousel')) customElements.define('jx-carousel', JxCarousel);
  if (!customElements.get('jx-pair-add')) customElements.define('jx-pair-add', JxPairAdd);
})();
